<script setup>
/**
 * ToggleSwitch — a GlToggle-compatible pill toggle.
 *
 * Props match GlToggle so a future swap to <GlToggle> requires no caller changes:
 *   - `modelValue`    (Boolean) — current state
 *   - `label`         (String)  — accessible label (sr-only when labelPosition="hidden")
 *   - `disabled`      (Boolean)
 *   - `labelPosition` ("left" | "hidden") — "left" renders label inline to the left
 *   - `help`          (String)  — help text displayed below the toggle
 *   - `isLoading`     (Boolean) — loading state, disables toggle with reduced opacity
 *
 * Emits `update:modelValue` with the new Boolean on click.
 */
const { disabled, help, isLoading, label, labelPosition, modelValue } = defineProps({
  disabled: { default: false, type: Boolean },
  help: { default: null, type: String },
  isLoading: { default: false, type: Boolean },
  label: { default: null, type: String },
  labelPosition: { default: "left", type: String },
  modelValue: { default: false, type: Boolean }
});

const emit = defineEmits(["update:modelValue"]);

function toggle() {
  if (!disabled && !isLoading) {
    emit("update:modelValue", !modelValue);
  }
}
</script>

<template>
  <div class="relative">
    <div :class="['inline-flex items-center gap-2', isLoading && 'opacity-50']">
      <span
        v-if="label"
        :class="labelPosition === 'hidden' ? 'sr-only' : 'text-xs text-[var(--gl-text-color-subtle)]'"
        data-testid="toggle-label"
      >
        {{ label }}
      </span>
      <button
        type="button"
        role="switch"
        :aria-checked="modelValue ? 'true' : 'false'"
        :aria-label="labelPosition === 'hidden' ? label : undefined"
        :disabled="disabled || isLoading"
        :class="['gl-toggle', modelValue && 'is-checked', (disabled || isLoading) && 'is-disabled']"
        data-testid="toggle-switch"
        @click.prevent="toggle"
      >
        <span class="toggle-icon" aria-hidden="true" />
      </button>
    </div>
    <p v-if="help" class="text-xs text-[var(--gl-text-color-subtle)] mt-1">
      {{ help }}
    </p>
  </div>
</template>
